import type { MerchantPreset } from './types';
import { NEUTRAL_MERCHANT_ID, NEUTRAL_MERCHANT_PRESET } from './neutral';
import { STATIC_MERCHANT_PRESETS } from './static';

type PresetLoader = () => Promise<{ default: MerchantPreset }>;

/** Lazy chunks for built-in merchants; keeps merchant copy out of the main bundle. */
const LAZY_MERCHANT_LOADERS: Record<string, PresetLoader> = {
  nomakeupmakeup: () => import(/* webpackChunkName: "merchant-nmm" */ './nomakeupmakeup'),
  ysebeauty: () => import(/* webpackChunkName: "merchant-yse" */ './ysebeauty'),
};

const customPresets = new Map<string, MerchantPreset>();

export function normalizeMerchantId(id: unknown): string {
  return typeof id === 'string' ? id.trim().toLowerCase() : '';
}

function normalizeHostname(hostname: unknown): string {
  return typeof hostname === 'string' ? hostname.trim().toLowerCase() : '';
}

/** `hostnames` entries are exact hosts or `.suffix` entries (`endsWith`). */
export function hostnameMatches(hostname: string, hostnames?: string[]): boolean {
  const host = normalizeHostname(hostname);
  if (!host || !Array.isArray(hostnames)) return false;
  return hostnames.some(entry => {
    const pattern = normalizeHostname(entry);
    if (!pattern) return false;
    return pattern.startsWith('.') ? host.endsWith(pattern) : host === pattern;
  });
}

/** Register a custom preset so it can be referenced by id (and matched by hostname). */
export function registerMerchantPreset(preset: MerchantPreset): void {
  const id = normalizeMerchantId(preset?.id);
  if (!id) throw new Error('registerMerchantPreset: preset.id is required');
  customPresets.set(id, preset);
}

/** Explicit id wins; else hostname match (custom, then built-in); else neutral. */
export function resolveMerchantPresetId({
  explicit,
  hostname,
}: {
  explicit?: string | null;
  hostname?: string | null;
}): string {
  const explicitId = normalizeMerchantId(explicit);
  if (explicitId) return explicitId;
  const host = normalizeHostname(hostname);
  if (host) {
    for (const [id, preset] of customPresets) {
      if (hostnameMatches(host, preset.hostnames)) return id;
    }
    for (const [id, preset] of Object.entries(STATIC_MERCHANT_PRESETS)) {
      if (hostnameMatches(host, preset.hostnames)) return id;
    }
  }
  return NEUTRAL_MERCHANT_ID;
}

/**
 * Synchronously available preset for `id`: the registered custom object, the
 * eager part of a built-in preset, or neutral. Safe to use before the lazy
 * chunk has resolved (no flash of neutral behaviour for the eager flags).
 */
export function getStaticMerchantPreset(id: string): MerchantPreset {
  const key = normalizeMerchantId(id);
  const custom = customPresets.get(key);
  if (custom) return custom;
  const eager = STATIC_MERCHANT_PRESETS[key];
  if (eager) return eager;
  return NEUTRAL_MERCHANT_PRESET;
}

/** Full preset for `id`; lazy-loads built-in merchants, resolves neutral otherwise. */
export async function loadMerchantPreset(id: string): Promise<MerchantPreset> {
  const key = normalizeMerchantId(id);
  const custom = customPresets.get(key);
  if (custom) return custom;
  const loader = LAZY_MERCHANT_LOADERS[key];
  if (loader) {
    const mod = await loader();
    return mod.default;
  }
  return NEUTRAL_MERCHANT_PRESET;
}
